<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink } from 'vue-router'

import AppIcon from '@/components/common/AppIcon.vue'
import { useUiStore } from '@/stores/ui'
import { useUserStore } from '@/stores/user'

/**
 * Navigation of the admin panel.
 *
 * Static on purpose: unlike the user-facing sidebar, these entries do not come
 * from `GET /menu` — the panel is what lets you *edit* that menu, so it cannot
 * depend on it.
 */
const props = withDefaults(
  defineProps<{
    /** Used by the mobile drawer, where narrowing to icons makes no sense. */
    forceExpanded?: boolean
  }>(),
  { forceExpanded: false },
)

const { t } = useI18n()
const ui = useUiStore()
const user = useUserStore()
const collapsed = computed(() => !props.forceExpanded && ui.sidebarCollapsed)

interface AdminNavEntry {
  name: string
  labelKey: string
  icon: string
  /** Sections editors must not see: they are admin-only. */
  adminOnly?: boolean
}

const ENTRIES: AdminNavEntry[] = [
  { name: 'admin-home', labelKey: 'admin.nav.home', icon: 'mdi-home' },
  { name: 'admin-menus', labelKey: 'common.fields.menu', icon: 'mdi-menu', adminOnly: true },
  {
    name: 'admin-users',
    labelKey: 'common.fields.users',
    icon: 'mdi-account-multiple-outline',
    adminOnly: true,
  },
  { name: 'admin-news', labelKey: 'common.fields.news', icon: 'mdi-newspaper-variant-outline' },
  { name: 'admin-servers', labelKey: 'common.fields.servers', icon: 'mdi-server' },
  { name: 'admin-jobs', labelKey: 'admin.nav.jobs', icon: 'mdi-clock-outline', adminOnly: true },
  { name: 'admin-groups', labelKey: 'common.fields.groups', icon: 'mdi-group' },
  { name: 'admin-labels', labelKey: 'common.fields.labels', icon: 'mdi-translate' },
  { name: 'admin-discord', labelKey: 'common.fields.discord', icon: 'mdi-discord' },
]

const entries = computed(() => ENTRIES.filter((entry) => !entry.adminOnly || user.isAdmin))
</script>

<template>
  <aside
    class="chrome-surface flex shrink-0 flex-col border-r border-black/30 transition-[width] duration-200"
    :class="collapsed ? 'w-16' : 'w-64'"
    :aria-label="t('admin.nav.label')"
  >
    <nav class="flex-1 overflow-y-auto p-2">
      <ul class="space-y-0.5">
        <li v-for="entry in entries" :key="entry.name">
          <RouterLink :to="{ name: entry.name }" custom>
            <template #default="{ navigate, href, isExactActive }">
              <a
                :href="href"
                class="chrome-item"
                :class="{ 'chrome-item-active': isExactActive }"
                :aria-current="isExactActive ? 'page' : undefined"
                :title="collapsed ? t(entry.labelKey) : undefined"
                @click="navigate"
              >
                <AppIcon :name="entry.icon" class="text-lg" />
                <span v-if="!collapsed" class="truncate">{{ t(entry.labelKey) }}</span>
              </a>
            </template>
          </RouterLink>
        </li>
      </ul>

      <hr class="my-2 border-white/10" aria-hidden="true" />

      <!-- Way out of the panel, back to the ordinary site. -->
      <ul>
        <li>
          <RouterLink :to="{ name: 'home' }" custom>
            <template #default="{ navigate, href }">
              <a
                :href="href"
                class="chrome-item text-gold-300 hover:text-gold-200"
                :title="collapsed ? t('admin.nav.backToSite') : undefined"
                @click="navigate"
              >
                <AppIcon name="mdi-arrow-left-circle-outline" class="text-lg" />
                <span v-if="!collapsed" class="truncate">{{ t('admin.nav.backToSite') }}</span>
              </a>
            </template>
          </RouterLink>
        </li>
      </ul>
    </nav>

    <!-- Bottom action: collapse on desktop, close on the mobile drawer. -->
    <div class="border-t border-white/10 p-2">
      <button
        v-if="props.forceExpanded"
        type="button"
        class="chrome-item w-full cursor-pointer"
        :aria-label="t('common.close')"
        @click="ui.closeMobileSidebar()"
      >
        <AppIcon name="mdi-close" class="text-lg" />
        <span class="truncate">{{ t('common.close') }}</span>
      </button>
      <button
        v-else
        type="button"
        class="chrome-item w-full cursor-pointer"
        :aria-label="collapsed ? t('sidebar.expand') : t('sidebar.collapse')"
        :title="collapsed ? t('sidebar.expand') : t('sidebar.collapse')"
        @click="ui.toggleSidebar()"
      >
        <AppIcon
          :name="collapsed ? 'mdi-chevron-double-right' : 'mdi-chevron-double-left'"
          class="text-lg"
        />
        <span v-if="!collapsed" class="truncate">{{ t('sidebar.collapse') }}</span>
      </button>
    </div>
  </aside>
</template>
